import { BaseWorkflowProcessor } from './BaseWorkflowProcessor';
import {
    Field,
    PatchWorkflowData,
    Product,
    WorkflowConfig,
    WorkflowConfigField,
    WorkflowData
} from '@monorepo/interfaces';
import { FormGroup } from '@angular/forms';

/**
 * Processor implementation for updating existing workflows
 *
 * @description This class implements the workflow processor for the update operation.
 * It builds PatchWorkflowData objects that preserve existing IDs and handle product
 * replacement according to the update requirements.
 */
export class PatchWorkflowProcessor extends BaseWorkflowProcessor<PatchWorkflowData, Field, Product> {
    /**
     * Reference to the existing workflow data being updated
     * @private
     */
    private existingWorkflowData: WorkflowData;

    /**
     * Sets the existing workflow data to use as reference during processing
     *
     * @description This method must be called before processing a workflow form
     * to ensure the processor has access to the existing workflow data for ID preservation
     * and proper product handling.
     *
     * @param data {WorkflowData} - The existing workflow data to reference during processing
     * @returns {void}
     */
    public setExistingWorkflowData(data: WorkflowData): void {
        this.existingWorkflowData = data;
    }

    /**
     * Initializes the products array with a copy of existing products
     *
     * @description For updates, we start with the existing products collection
     * so we can properly update or replace products as needed.
     *
     * @returns {Map<string, Product>} The map of existing products from the workflow being updated.
     */
    getWorkflowDetailsProducts(): Map<string, Product> {
        const products = new Map<string, Product>();
        if (this.existingWorkflowData?.products) {
            this.existingWorkflowData.products.forEach(product => {
                products.set(product.name, this.createProductObject(product.fields, product.name, product));
            });
        }
        return products;
    }

    /**
     * Initializes the fields array with a copy of existing fields
     *
     * @description For updates, we start with the existing fields collection
     * so we can properly update fields while preserving their IDs.
     *
     * @returns {Field[]} The existing fields from the workflow being updated.
     */
    getWorkflowDetailsFields(): Field[] {
        return this.existingWorkflowData?.fields ? [...this.existingWorkflowData.fields] : [];
    }

    /**
     * Creates a field object that preserves the existing field ID if available
     *
     * @param fieldConfig {WorkflowConfigField} - The field configuration from the workflow definition
     * @param value {any} - The value extracted from the form control
     * @returns A Field object with ID preserved (if found in existing data) and updated value
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    createFieldObject(fieldConfig: WorkflowConfigField, value: any): Field {
        // If we have existing data, try to find this field's ID
        const existingField = this.existingWorkflowData?.fields.find(f => f.name === fieldConfig.name);

        return {
            id: existingField?.id, // Add ID if it exists in the current data
            name: fieldConfig.name,
            type: fieldConfig.fieldType,
            value: value
        };
    }

    /**
     * Creates a product object that preserves the existing product ID if available
     *
     * @param fields {Field[]} - Array of field objects to include in the product
     * @param name {string} - The name of the product
     * @param existingProduct {Product} - Optional existing product to extract ID from
     * @returns A Product object with ID preserved (if available) and updated fields
     */
    createProductObject(fields: Field[], name: string, existingProduct?: Product): Product {
        // If we have existing data, try to find this product's ID
        const existingProductData = existingProduct || this.existingWorkflowData?.products.find(p => p.name === name);

        return {
            id: existingProductData?.id, // Add ID if it exists in the current data
            fields,
            name
        };
    }

    /**
     * Creates the final patch workflow data object
     *
     * @param config {WorkflowConfig} - The workflow configuration used for processing
     * @param fields {Field[]} - Collection of processed field objects with preserved IDs
     * @param products {Product[]} - Collection of processed product objects with preserved IDs
     * @returns A PatchWorkflowData object ready for submission to update an existing workflow
     */
    createResultObject(config: WorkflowConfig, fields: Field[], products: Product[]): PatchWorkflowData {
        return {
            name: config.name,
            configType: config.type,
            configId: config.id,
            description: fields.find(field => field.name === 'description')?.value || '',
            startDate: new Date(fields.find(field => field.name === 'start-date')?.value || ''),
            endDate: new Date(fields.find(field => field.name === 'end-date')?.value || ''),
            fields,
            products
        } as PatchWorkflowData;
    }

    /**
     * Finds or creates product by productName and aggregates fields for it.
     * @param {Map<string, Product>} products - Map of current products.
     * @param {string} productName - Name of the product to find or create.
     * @returns {Product} Product entry for aggregation.
     */
    private getOrCreateProduct(products: Map<string, Product>, productName: string): Product {
        if (!products.has(productName)) {
            const existingProduct = this.existingWorkflowData?.products.find(
                existingProduct => existingProduct.name === productName
            );
            products.set(productName, this.createProductObject([], productName, existingProduct));
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return products.get(productName)!;
    }

    /**
     * Handles product creation/aggregation for patch (preserving IDs).
     * Product name is always the value of 'coreMlfb'.
     * @param {Field} field - The processed field object.
     * @param {Map<string, Product>} products - The map of products to aggregate into.
     * @param {FormGroup} productFormGroup - The form group containing the field value.
     * @returns {void}
     */
    protected override addFieldToProduct(
        field: Field,
        products: Map<string, Product>,
        productFormGroup: FormGroup
    ): void {
        this.coreMlfbControlValue.split(',').forEach(productName => {
            const trimmedProductName = productName.trim();
            if (!trimmedProductName) return; // Skip empty names
            const product = this.getOrCreateProduct(products, trimmedProductName);
            const productGroup = productFormGroup.get(`product-${product.name}`) as FormGroup;
            const productFieldValue = productGroup ? productGroup.get(field.name)?.value : null;
            // Replace/add the field in the product.fields
            const fieldIdx = product.fields.findIndex(existingField => existingField.name === field.name);
            if (fieldIdx >= 0) {
                product.fields[fieldIdx].value =
                    !!productFieldValue && productFieldValue !== '' ? productFieldValue : field.value;
            } else {
                product.fields.push(field);
            }
        });
    }
    /**
     * Merges a field into the fields array, replacing by id if present.
     * @param {Field[]} fields - The current collection of fields.
     * @param {Field} newField - The new field to merge.
     * @returns {void}
     */
    protected override mergeField(fields: Field[], newField: Field): void {
        if (newField.id) {
            const index = fields.findIndex(existingField => existingField.id === newField.id);
            if (index !== -1) {
                fields[index] = newField;
                return;
            }
        }
        fields.push(newField);
    }
}
